// AI Orchestration — Live Conversations debug panel (super-admin + supervisor).
// See every bot-touched chat, its state, the bot's reply + whether it was
// actually delivered, and the full orchestration timeline.
import { useEffect, useState, useCallback } from "react";
import { Loader2, MessagesSquare, Bot, User, AlertTriangle, CheckCircle2, Clock, X, Search } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATE_BADGE = {
  bot_handling: { label: "Bot", cls: "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300" },
  pending_assignment: { label: "Pending", cls: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" },
  agent_assigned: { label: "Assigned", cls: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300" },
  bot_resolved: { label: "Resolved", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" },
  closed: { label: "Closed", cls: "bg-muted text-muted-foreground" },
};
const REASON = {
  human_request: "Human requested", negative_sentiment: "Negative sentiment",
  low_kb_confidence: "Bot couldn't answer", max_bot_turns: "Max bot turns",
  draft_review: "Email draft", no_bot: "Bot off", bot_unavailable: "Bot offline (LLM)",
  send_failed: "Auto-send failed", reopened: "Reopened", bot_reply: "Bot replied",
  claimed: "Claimed", released: "Released", agent_closed: "Closed by agent",
};
const DELIVERY = {
  sent: { label: "Delivered", cls: "text-emerald-600 dark:text-emerald-400" },
  send_failed: { label: "Send failed", cls: "text-red-600 dark:text-red-400" },
  draft: { label: "Draft", cls: "text-muted-foreground" },
};
const TABS = [
  { key: "all", label: "All" },
  { key: "bot_handling", label: "Bot" },
  { key: "pending_assignment", label: "Pending" },
  { key: "agent_assigned", label: "Assigned" },
  { key: "closed", label: "Closed" },
];

function fmtTime(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function OrchConversations() {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/orchestrator/conversations", { params: { state, q, limit: 80 } });
      setRows(r.data.conversations || []);
    } catch (e) { if (!silent) toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [state, q]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 7000);
    return () => clearInterval(t);
  }, [load]);

  async function openDetail(id) {
    setDetailLoading(true); setDetail({ id });
    try {
      const r = await api.get(`/orchestrator/conversations/${id}`);
      setDetail(r.data);
    } catch (e) { toast.error(formatErr(e)); setDetail(null); }
    finally { setDetailLoading(false); }
  }

  return (
    <Card className="p-4 mt-5" data-testid="orch-conversations">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="font-display font-bold flex items-center gap-2"><MessagesSquare className="w-4 h-4 text-primary" /> Live Conversations</div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input data-testid="orch-conv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contact…" className="h-8 pl-8 w-52" />
        </div>
      </div>
      <div className="flex gap-1.5 mb-3 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`orch-conv-tab-${t.key}`} onClick={() => setState(t.key)}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${state === t.key ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/50"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground" data-testid="orch-conv-empty">No orchestrated conversations yet.</div>
      ) : (
        <div className="divide-y max-h-96 overflow-y-auto">
          {rows.map((c) => {
            const sb = STATE_BADGE[c.orch_state] || STATE_BADGE.closed;
            const dl = DELIVERY[c.bot_delivery];
            return (
              <button key={c.id} onClick={() => openDetail(c.id)} data-testid={`orch-conv-row-${c.id}`}
                className="w-full text-left py-2.5 flex items-center justify-between gap-3 hover:bg-muted/30 px-1 rounded">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate flex items-center gap-2">
                    {c.contact_name || "Unknown"}
                    <span className="text-[10px] text-muted-foreground capitalize">· {c.primary_channel}</span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {c.escalation_reason ? (REASON[c.escalation_reason] || c.escalation_reason) : (c.last_bot_reply ? `🤖 ${c.last_bot_reply.slice(0, 60)}` : `${c.msg_count} msgs`)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {dl && <span className={`text-[10px] font-bold ${dl.cls}`}>{dl.label}</span>}
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sb.cls}`}>{sb.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="orch-conv-detail">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detail?.contact_name || "Conversation"}
              {detail?.orch_state && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${(STATE_BADGE[detail.orch_state] || STATE_BADGE.closed).cls}`}>{(STATE_BADGE[detail.orch_state] || {}).label}</span>}
            </DialogTitle>
          </DialogHeader>
          {detailLoading || !detail?.messages ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : (
            <div className="overflow-y-auto space-y-4 pr-1">
              {detail.escalation_reason && (
                <div className="text-xs bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 rounded-md px-3 py-2 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" /> Escalated: {REASON[detail.escalation_reason] || detail.escalation_reason}
                  {detail.escalation_detail ? ` — ${detail.escalation_detail}` : ""}
                </div>
              )}
              {/* Messages */}
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Messages</div>
                <div className="space-y-2">
                  {detail.messages.map((m, i) => {
                    const isBot = m.from === "bot";
                    const isCust = m.from === "customer" || m.direction === "inbound";
                    const dl = DELIVERY[m.delivery_status];
                    return (
                      <div key={i} className={`flex gap-2 ${isCust ? "" : "flex-row-reverse"}`}>
                        <div className={`w-6 h-6 rounded-full grid place-items-center shrink-0 ${isBot ? "bg-sky-100 text-sky-600" : isCust ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"}`}>
                          {isBot ? <Bot className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
                        </div>
                        <div className={`max-w-[75%] rounded-lg px-3 py-1.5 text-sm ${isCust ? "bg-muted" : "bg-primary/10"}`}>
                          <div className="whitespace-pre-wrap">{m.body}</div>
                          <div className="mt-0.5 flex items-center gap-2 text-[10px] text-muted-foreground">
                            <span>{fmtTime(m.ts)}</span>
                            {isBot && dl && <span className={`font-bold ${dl.cls}`}>· {dl.label}</span>}
                            {isBot && m.error && <span className="text-red-500">· {m.error}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Timeline */}
              {(detail.orch_history || []).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Orchestration timeline</div>
                  <div className="space-y-1.5">
                    {detail.orch_history.map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs" data-testid={`orch-timeline-${i}`}>
                        <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="font-medium">{(STATE_BADGE[h.state] || {}).label || h.state}</span>
                        {h.reason && <span className="text-muted-foreground">· {REASON[h.reason] || h.reason}</span>}
                        <span className="text-muted-foreground/60 ml-auto">{fmtTime(h.ts)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
